import React, { useEffect, useRef, useState } from 'react';

const styles = require('./NlSearchChat.module.scss');

interface Chips {
  [key: string]: unknown;
}

interface Usage {
  input_tokens: number;
  output_tokens: number;
  cost: number | null;
}

interface Message {
  role: 'user' | 'assistant';
  text: string;
  chips?: Chips;
  reset?: boolean;
  unresolved?: string[];
  usage?: Usage | null;
}

interface Props {
  initialChips: Chips;
  endpoint: string;
  searchPath: string;
}

interface ApiResponse {
  chips: Chips;
  explanation: string;
  reset: boolean;
  unresolved: string[];
  usage: Usage | null;
  search_url: string | null;
}

const HISTORY_STORAGE_KEY = 'bo-nl-search-history';
const DEFAULT_SORT_ATTR = 'people.id-desc';

// search.js.erb dispatches this after every results refresh (search, refinement,
// chip removal, classic form, pagination) so the sidebar count stays in sync
// with the pagination total.
const PEOPLE_COUNT_EVENT = 'bo:people-count';

// The translator only reads the last few turns (history.last(8) server-side),
// so cap what we send to keep the request payload bounded as the thread grows.
const HISTORY_SEND_LIMIT = 10;

// Internal search modifiers that travel in the URL but aren't user-facing
// filters, so they shouldn't render as removable chips.
const HIDDEN_CHIP_KEYS = ['unconfirmed_skills'];

function loadHistory(): Message[] {
  try {
    const raw = sessionStorage.getItem(HISTORY_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Message[]) : [];
  } catch (_e) {
    return [];
  }
}

function saveHistory(history: Message[]): void {
  sessionStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
}

function csrfToken(): string {
  const el = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null;
  return el ? el.content : '';
}

// Compact, list-price cost estimate for one interaction, e.g. "~$0.0021 · 1.4k tokens".
function formatUsage(usage: Usage): string {
  const total = usage.input_tokens + usage.output_tokens;
  const tokens = total >= 1000 ? `${(total / 1000).toFixed(1)}k tokens` : `${total} tokens`;
  return usage.cost == null ? tokens : `~$${usage.cost.toFixed(4)} · ${tokens}`;
}

// The thread itself doesn't scroll — the surrounding panel does. Walk up to it.
function scrollableAncestor(node: HTMLElement | null): HTMLElement | null {
  let el = node?.parentElement ?? null;
  while (el) {
    const overflowY = getComputedStyle(el).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return el;
    el = el.parentElement;
  }
  return null;
}

function currentSortAttr(): string {
  const urlSort = new URLSearchParams(window.location.search).get('sort_attr');
  const formSort = (document.getElementById('sort_attr') as HTMLInputElement | null)?.value;
  return urlSort || formSort || DEFAULT_SORT_ATTR;
}

export default function NlSearchChat(props: Props) {
  const [history, setHistory] = useState<Message[]>(loadHistory);
  const [chips, setChips] = useState<Chips>(props.initialChips || {});
  // The match counter only appears once the recruiter starts searching (null =
  // hidden). prevCount drives the "from N" baseline; prevCountRef tracks the
  // running total across the event-driven updates. It starts empty so the first
  // search shows just the count — the delta is only meaningful once there's a
  // prior search to refine against.
  const [peopleCount, setPeopleCount] = useState<number | null>(null);
  const [prevCount, setPrevCount] = useState<number | null>(null);
  const prevCountRef = useRef<number | null>(null);
  const suppressCountRef = useRef(false);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const searchEndpoint = `${props.searchPath.replace(/\/$/, '')}/search.js`;

  // Auto-grow the textarea with its content, up to the CSS max-height (160px).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [draft]);

  // Keep the newest message (and the loading indicator) in view: scroll the
  // panel to the bottom whenever a message is added or loading toggles.
  useEffect(() => {
    const scroller = scrollableAncestor(threadRef.current);
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }, [history.length, loading]);

  // The total comes from the server after the results panel re-renders, so we
  // listen for the count it broadcasts rather than computing it client-side.
  useEffect(() => {
    const onCount = (event: Event): void => {
      const detail = (event as CustomEvent<{ count?: number }>).detail;
      if (!detail || typeof detail.count !== 'number') return;

      const next = detail.count;
      // A reset re-runs the unfiltered search; treat its count as the new
      // baseline and keep the counter hidden until the next real search.
      if (suppressCountRef.current) {
        suppressCountRef.current = false;
        prevCountRef.current = next;
        return;
      }
      setPrevCount(prevCountRef.current);
      setPeopleCount(next);
      prevCountRef.current = next;
    };
    document.addEventListener(PEOPLE_COUNT_EVENT, onCount);
    return () => document.removeEventListener(PEOPLE_COUNT_EVENT, onCount);
  }, []);

  const refreshResults = (
    nextChips: Chips,
    searchUrl: string,
    sortAttr: string = currentSortAttr()
  ): Promise<void> => {
    window.history.pushState({}, '', searchUrl);
    if (window.toggleSpinner) window.toggleSpinner(true);

    // `/search.js` is the backoffice's Rails UJS endpoint: it returns a JS
    // response (search_results.js.erb) that re-renders the results panel in
    // place. We keep jQuery here to execute that script, mirroring how the
    // classic filter form (remote: true) refreshes the same panel.
    return new Promise((resolve, reject) => {
      $.ajax({
        url: searchEndpoint,
        type: 'POST',
        dataType: 'script',
        data: { q: nextChips, sort_attr: sortAttr },
      })
        .done(() => resolve())
        .fail(() => {
          if (window.toggleSpinner) window.toggleSpinner(false);
          reject(new Error('Search failed'));
        });
    });
  };

  const send = async (message: string): Promise<void> => {
    setLoading(true);
    setError(null);
    if (window.toggleSpinner) window.toggleSpinner(true);

    const userMessage: Message = { role: 'user', text: message };
    const afterUser = [...history, userMessage];
    setHistory(afterUser);
    saveHistory(afterUser);

    try {
      const response = await fetch(props.endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({ message, current_chips: chips, history: afterUser.slice(-HISTORY_SEND_LIMIT) }),
      });

      if (!response.ok) {
        throw new Error(`Search failed with status ${response.status}`);
      }

      const data: ApiResponse = await response.json();

      const assistantMessage: Message = {
        role: 'assistant',
        text: data.explanation,
        chips: data.chips,
        reset: data.reset,
        unresolved: data.unresolved,
        usage: data.usage,
      };
      const next = [...afterUser, assistantMessage];
      setHistory(next);
      saveHistory(next);

      if (data.search_url) {
        setChips(data.chips);
        await refreshResults(data.chips, data.search_url);
      }
    } catch (e) {
      setError('Something went wrong — please try again.');
    } finally {
      if (window.toggleSpinner) window.toggleSpinner(false);
      setLoading(false);
    }
  };

  const submitDraft = (): void => {
    const trimmed = draft.trim();
    if (!trimmed || loading) return;
    setDraft('');
    void send(trimmed);
  };

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    submitDraft();
  };

  // Enter sends; Shift+Enter inserts a newline (chat-composer convention).
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitDraft();
    }
  };

  // Shareable search URL for a chip set, mirroring the `search_url` the
  // translator returns for a sent message.
  const buildSearchUrl = (nextChips: Chips): string => {
    const url = new URL(props.searchPath, window.location.origin);
    Object.entries(nextChips).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        v.forEach(item => url.searchParams.append(`q[${k}][]`, String(item)));
      } else if (v !== undefined && v !== null && v !== '') {
        url.searchParams.set(`q[${k}]`, String(v));
      }
    });
    return url.toString();
  };

  const removeChip = async (key: string): Promise<void> => {
    const next: Chips = { ...chips };
    delete next[key];

    // Drop the unconfirmed-skills modifier once no skill filter remains.
    if (!next.skills_tag_id_eq_any && !next.has_all_skills) {
      delete next.unconfirmed_skills;
    }
    setChips(next);
    setError(null);

    // Refresh results in place via the same AJAX path as sending a message,
    // not a full-page reload, so the conversation and scroll position survive.
    try {
      await refreshResults(next, buildSearchUrl(next));
    } catch (_e) {
      setError('Something went wrong — please try again.');
    }
  };

  const chipLabel = (key: string, value: unknown): string => {
    if (Array.isArray(value)) return `${key}: ${value.join(', ')}`;
    return `${key}: ${String(value)}`;
  };

  const resetSearch = async (): Promise<void> => {
    sessionStorage.removeItem(HISTORY_STORAGE_KEY);
    setHistory([]);
    setChips({});
    setError(null);
    // Back to the "before searching" state: hide the counter and let the
    // reset's own refresh re-seed the baseline without showing a delta.
    setPeopleCount(null);
    setPrevCount(null);
    suppressCountRef.current = true;
    setLoading(true);

    try {
      await refreshResults({}, props.searchPath, DEFAULT_SORT_ATTR);
    } catch (_e) {
      setError('Something went wrong — please try again.');
    } finally {
      setLoading(false);
    }
  };

  const hasMessages = history.length > 0;
  const hasState = hasMessages || Object.keys(chips).length > 0;
  const visibleChips = Object.entries(chips).filter(([k]) => !HIDDEN_CHIP_KEYS.includes(k));
  const countDelta = peopleCount != null && prevCount != null ? peopleCount - prevCount : null;

  return (
    <div className={styles.container}>
      {error && (
        <div className={styles.errorBanner} role="alert">
          {error}
        </div>
      )}

      <div className={styles.thread} role="log" aria-live="polite" aria-busy={loading} ref={threadRef}>
        {history.map((m, i) => {
          const isLatestAssistant = m.role === 'assistant' && i === history.length - 1;
          const showChips = isLatestAssistant && visibleChips.length > 0;
          const startsTurn = m.role === 'user' && i > 0;
          const rowClass = [
            styles.row,
            m.role === 'user' ? styles.rowUser : styles.rowAi,
            startsTurn ? styles.turnStart : '',
          ].join(' ');

          return (
            <React.Fragment key={i}>
              {m.role === 'assistant' && m.reset && <div className={styles.resetDivider}>New search started</div>}

              <div className={rowClass}>
                <div className={`${styles.bubble} ${m.role === 'user' ? styles.userBubble : styles.aiBubble}`}>
                  {m.text}
                </div>
              </div>

              {m.role === 'assistant' && m.usage && (
                <div className={styles.resultRow}>
                  <small className={styles.usageMeta} title="List-price estimate; excludes caching and negotiated rates">
                    {formatUsage(m.usage)}
                  </small>
                </div>
              )}

              {showChips && (
                <div className={styles.resultRow}>
                  <div className={styles.chips}>
                    {visibleChips.map(([k, v]) => (
                      <span key={k} className={styles.chip}>
                        {chipLabel(k, v)}
                        <button
                          type="button"
                          className={styles.chipRemove}
                          onClick={() => { void removeChip(k); }}
                          aria-label={`Remove ${k} filter`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {m.unresolved && m.unresolved.length > 0 && (
                <div className={styles.resultRow}>
                  <small className={styles.unresolvedNote}>Couldn&apos;t match: {m.unresolved.join(', ')}</small>
                </div>
              )}
            </React.Fragment>
          );
        })}

        {loading && (
          <div className={`${styles.row} ${styles.rowAi}`}>
            <div className={`${styles.bubble} ${styles.aiBubble} ${styles.typing}`} aria-hidden="true">
              <span className={styles.dot} />
              <span className={styles.dot} />
              <span className={styles.dot} />
            </div>
          </div>
        )}
      </div>

      <div className={styles.composer}>
        {peopleCount != null && (
          <div className={styles.matchBanner} role="status" aria-live="polite">
            <span className={styles.matchCount}>{peopleCount.toLocaleString()}</span>
            {' '}
            <span className={styles.matchLabel}>{peopleCount === 1 ? 'match' : 'matches'}</span>
            {countDelta != null && countDelta !== 0 && (
              <>
                {' '}
                <span
                  className={`${styles.matchDelta} ${countDelta < 0 ? styles.matchDeltaDown : styles.matchDeltaUp}`}
                >
                  {countDelta < 0 ? '↓' : '↑'} {Math.abs(countDelta).toLocaleString()}
                </span>
                {' '}
                <span className={styles.matchFrom}>from {prevCount?.toLocaleString()}</span>
              </>
            )}
          </div>
        )}

        {!hasMessages && (
          <label className={styles.inputLabel} htmlFor="bo-nl-search-input">
            Describe who you&apos;re looking for
          </label>
        )}
        <form onSubmit={submit} className={styles.inputShell}>
          <textarea
            id="bo-nl-search-input"
            ref={textareaRef}
            value={draft}
            rows={3}
            maxLength={2000}
            disabled={loading}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label={hasMessages ? "Describe who you're looking for" : undefined}
            placeholder={hasState ? 'e.g. with a login in the last 6 months' : 'e.g. Rails developers with at least 4 years of experience in Portugal'}
          />
          <button type="submit" className={styles.sendButton} disabled={loading || !draft.trim()} aria-label="Send">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 19V5M5 12l7-7 7 7" />
            </svg>
          </button>
        </form>

        {loading && (
          <p className={styles.srOnly} role="status">
            Searching…
          </p>
        )}

        {hasState && (
          <div className={styles.refineRow}>
            <span className={styles.refineHint}>New messages refine this search.</span>
            <button
              type="button"
              className={styles.resetButton}
              onClick={resetSearch}
              disabled={loading}
            >
              <span aria-hidden="true">↺</span> Start over
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
